import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus, Trash2, Settings, MapPin, Syringe, 
  CreditCard, Pill, RefreshCw, DollarSign, Edit2, Check, X, ListOrdered
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const categories = [
  { id: 'comunas', label: 'Comunas', icon: MapPin },
  { id: 'tipo_vacunacion', label: 'Tipos de Vacunación', icon: Settings },
  { id: 'dosis', label: 'Dosis (Generales)', icon: Pill },
  { id: 'vacuna', label: 'Vacunas', icon: Syringe, hasPrice: true, hasDoses: true },
  { id: 'tipo_pago', label: 'Tipos de Pago', icon: CreditCard },
];

const CatalogsPage = () => {
  const [catalogs, setCatalogs] = useState({});
  const [loading, setLoading] = useState(true);
  const [newItems, setNewItems] = useState({});
  const [newPrices, setNewPrices] = useState({});
  const [deleteItem, setDeleteItem] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editingDoses, setEditingDoses] = useState(null);
  const [dosesList, setDosesList] = useState([]);
  const [esquemaDesc, setEsquemaDesc] = useState('');
  const [customDose, setCustomDose] = useState('');
  const [savingEsquema, setSavingEsquema] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [schedulesMap, setSchedulesMap] = useState({});
  const [dosisGenerales, setDosisGenerales] = useState([]);

  const fetchCatalogs = async () => {
    setLoading(true);
    try {
      const [response, schedulesRes] = await Promise.all([
        axios.get(`${API}/catalogs`, { withCredentials: true }),
        axios.get(`${API}/vaccine-schedules`, { withCredentials: true })
      ]);
      
      const grouped = response.data.reduce((acc, item) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item);
        return acc;
      }, {});

      // Store general doses for quick selection
      const dosis = response.data.filter(item => item.category === 'dosis').map(d => d.value);
      setDosisGenerales(dosis);

      const sMap = {};
      schedulesRes.data.forEach(s => { sMap[s.vacuna] = s; });
      setSchedulesMap(sMap);

      setCatalogs(grouped);
    } catch (error) {
      console.error('Error fetching catalogs:', error);
      toast.error('Error al cargar los catálogos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalogs();
  }, []);

  const handleSeedCatalogs = async () => {
    try {
      await axios.post(`${API}/seed/catalogs`, {}, { withCredentials: true });
      toast.success('Catálogos inicializados');
      fetchCatalogs();
    } catch (error) {
      toast.error('Error al inicializar catálogos');
    }
  };

  const handleAddItem = async (category) => {
    const value = newItems[category]?.trim();
    if (!value) {
      toast.error('Ingrese un valor');
      return;
    }

    try {
      const payload = { category, value };
      // Add price if it's a vaccine
      if (category === 'vacuna' && newPrices[category]) {
        payload.precio = parseFloat(newPrices[category]);
      }
      
      await axios.post(`${API}/catalogs`, payload, { withCredentials: true });
      toast.success('Item agregado');
      setNewItems({ ...newItems, [category]: '' });
      setNewPrices({ ...newPrices, [category]: '' });
      fetchCatalogs();
    } catch (error) {
      toast.error('Error al agregar');
    }
  };

  const handleUpdatePrice = async (itemId) => {
    if (!editPrice && editPrice !== 0) {
      toast.error('Ingrese un precio válido');
      return;
    }

    try {
      await axios.put(`${API}/catalogs/${itemId}?precio=${parseFloat(editPrice)}`, {}, {
        withCredentials: true
      });
      toast.success('Precio actualizado');
      setEditingPrice(null);
      setEditPrice('');
      fetchCatalogs();
    } catch (error) {
      toast.error('Error al actualizar precio');
    }
  };

  const openDosesDialog = async (item) => {
    const baseDoses = (item.dosis_disponibles || []).map(name => ({ name, dias: '' }));
    setDosesList(baseDoses);
    setEsquemaDesc('');
    setCustomDose('');
    setEditingDoses(item);
    try {
      const res = await axios.get(`${API}/vaccine-schedules/${encodeURIComponent(item.value)}`, {
        withCredentials: true
      });
      const sched = res.data;
      setEsquemaDesc(sched.descripcion || '');
      const withDias = baseDoses.map((d, i) => {
        if (i === 0) return d;
        const intv = (sched.intervalos || []).find(
          x => x.de_dosis === baseDoses[i - 1].name && x.a_dosis === d.name
        );
        return intv ? { ...d, dias: intv.dias } : d;
      });
      setDosesList(withDias);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  };

  const addDose = (name) => {
    const d = (name || '').trim();
    if (!d) return;
    if (dosesList.some(x => x.name === d)) {
      toast.error('Esa dosis ya está agregada');
      return;
    }
    setDosesList(prev => [...prev, { name: d, dias: '' }]);
    setCustomDose('');
  };

  const removeDose = (index) => {
    setDosesList(dosesList.filter((_, i) => i !== index));
  };

  const updateDoseDias = (index, value) => {
    const updated = [...dosesList];
    updated[index] = { ...updated[index], dias: value };
    setDosesList(updated);
  };

  const handleSaveEsquema = async () => {
    if (!editingDoses) return;
    setSavingEsquema(true);
    try {
      const intervalos = [];
      for (let i = 1; i < dosesList.length; i++) {
        const dias = parseInt(dosesList[i].dias);
        if (dias > 0) {
          intervalos.push({ de_dosis: dosesList[i - 1].name, a_dosis: dosesList[i].name, dias });
        }
      }
      await axios.put(`${API}/catalogs/${editingDoses.id}/esquema-completo`, {
        dosis_disponibles: dosesList.map(d => d.name),
        intervalos,
        descripcion: esquemaDesc
      }, { withCredentials: true });
      toast.success('Esquema guardado: dosis e intervalos actualizados');
      setEditingDoses(null);
      fetchCatalogs();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Error al guardar esquema');
    } finally {
      setSavingEsquema(false);
    }
  };

  const handleSyncEsquemas = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/catalogs/sync-esquemas`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchCatalogs();
    } catch (error) {
      console.error('Error syncing schedules:', error);
      toast.error('Error al sincronizar esquemas');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteItem) return;

    try {
      await axios.delete(`${API}/catalogs/${deleteItem.id}`, {
        withCredentials: true
      });
      toast.success('Item eliminado');
      fetchCatalogs();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleteItem(null);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
  };

  const isEmpty = Object.keys(catalogs).length === 0;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="catalogs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Catálogos</h1>
            <p className="text-slate-500 mt-1">Administra las listas maestras del sistema</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={fetchCatalogs}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
            {isEmpty && (
              <Button 
                onClick={handleSeedCatalogs}
                className="btn-accent gap-2"
                data-testid="seed-catalogs-btn"
              >
                <Plus className="w-4 h-4" />
                Inicializar Catálogos
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="loading-spinner w-10 h-10"></div>
          </div>
        ) : isEmpty ? (
          <Card className="p-12 text-center">
            <Settings className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              No hay catálogos configurados
            </h3>
            <p className="text-slate-500 mb-6">
              Inicializa los catálogos con datos predeterminados para comenzar
            </p>
            <Button onClick={handleSeedCatalogs} className="btn-accent" data-testid="init-catalogs-btn">
              Inicializar Catálogos
            </Button>
          </Card>
        ) : (
          <Tabs defaultValue="comunas" className="space-y-6">
            <TabsList className="bg-slate-100 flex-wrap h-auto p-1" data-testid="catalog-tabs">
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <TabsTrigger 
                    key={cat.id} 
                    value={cat.id}
                    className="gap-2 data-[state=active]:bg-white"
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{cat.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {categories.map(cat => (
              <TabsContent key={cat.id} value={cat.id}>
                <Card data-testid={`catalog-card-${cat.id}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {React.createElement(cat.icon, { className: "w-5 h-5" })}
                      {cat.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Add new item */}
                    <div className="flex flex-wrap gap-3 mb-6">
                      <div className="flex-1 min-w-[200px]">
                        <Input
                          placeholder={`Nuevo ${cat.label.toLowerCase().slice(0, -1)}...`}
                          value={newItems[cat.id] || ''}
                          onChange={(e) => setNewItems({...newItems, [cat.id]: e.target.value})}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddItem(cat.id)}
                          className="input-clinical"
                          data-testid={`input-new-${cat.id}`}
                        />
                      </div>
                      {cat.hasPrice && (
                        <div className="w-32">
                          <Input
                            type="number"
                            placeholder="Precio $"
                            value={newPrices[cat.id] || ''}
                            onChange={(e) => setNewPrices({...newPrices, [cat.id]: e.target.value})}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddItem(cat.id)}
                            className="input-clinical"
                            data-testid={`input-price-${cat.id}`}
                          />
                        </div>
                      )}
                      <Button 
                        onClick={() => handleAddItem(cat.id)}
                        className="gap-2"
                        data-testid={`add-btn-${cat.id}`}
                      >
                        <Plus className="w-4 h-4" />
                        Agregar
                      </Button>
                      {cat.hasDoses && (
                        <Button 
                          variant="outline"
                          onClick={handleSyncEsquemas}
                          disabled={syncing}
                          className="gap-2"
                          data-testid="sync-esquemas-btn"
                        >
                          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                          {syncing ? 'Sincronizando...' : 'Sincronizar Esquemas'}
                        </Button>
                      )}
                    </div>

                    {/* Items list */}
                    {cat.hasPrice ? (
                      // Vaccines with price and doses - table view
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left p-3 text-sm font-medium text-slate-600">Vacuna</th>
                              <th className="text-left p-3 text-sm font-medium text-slate-600">Esquema de Dosis</th>
                              <th className="text-right p-3 text-sm font-medium text-slate-600 w-32">Precio</th>
                              <th className="w-20"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(catalogs[cat.id] || []).map(item => (
                              <tr key={item.id} className="border-t hover:bg-slate-50" data-testid={`catalog-item-${item.id}`}>
                                <td className="p-3 text-sm text-slate-700 font-medium">{item.value}</td>
                                <td className="p-3">
                                  <div className="flex flex-wrap items-center gap-1">
                                    {item.dosis_disponibles && item.dosis_disponibles.length > 0 ? (
                                      item.dosis_disponibles.map((d, idx) => {
                                        const sched = schedulesMap[item.value];
                                        const intv = idx > 0 && sched
                                          ? (sched.intervalos || []).find(
                                              x => x.de_dosis === item.dosis_disponibles[idx - 1] && x.a_dosis === d
                                            )
                                          : null;
                                        return (
                                          <React.Fragment key={d}>
                                            {idx > 0 && (
                                              <span className="text-xs text-slate-400 font-mono">
                                                →{intv ? `${intv.dias}d` : ''}
                                              </span>
                                            )}
                                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                              {d}
                                            </Badge>
                                          </React.Fragment>
                                        );
                                      })
                                    ) : (
                                      <span className="text-xs text-slate-400">Sin esquema</span>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openDosesDialog(item)}
                                      className="h-6 w-6 p-0 ml-1 text-slate-400 hover:text-blue-600"
                                      data-testid={`edit-doses-${item.id}`}
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </td>
                                <td className="p-3 text-right">
                                  {editingPrice === item.id ? (
                                    <div className="flex items-center justify-end gap-2">
                                      <Input
                                        type="number"
                                        value={editPrice}
                                        onChange={(e) => setEditPrice(e.target.value)}
                                        className="w-24 h-8 text-sm"
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleUpdatePrice(item.id);
                                          if (e.key === 'Escape') setEditingPrice(null);
                                        }}
                                      />
                                      <Button size="sm" variant="ghost" onClick={() => handleUpdatePrice(item.id)} className="h-8 w-8 p-0 text-green-600">
                                        <Check className="w-4 h-4" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingPrice(null)} className="h-8 w-8 p-0 text-red-600">
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-end gap-2">
                                      <span className={`text-sm font-medium ${item.precio ? 'text-emerald-600' : 'text-slate-400'}`}>
                                        {formatCurrency(item.precio)}
                                      </span>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={() => { setEditingPrice(item.id); setEditPrice(item.precio || ''); }}
                                        className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600"
                                        data-testid={`edit-price-${item.id}`}
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  )}
                                </td>
                                <td className="p-3 text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                    onClick={() => setDeleteItem(item)}
                                    data-testid={`delete-btn-${item.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(!catalogs[cat.id] || catalogs[cat.id].length === 0) && (
                          <div className="text-center py-8 text-slate-500">
                            <p>No hay vacunas registradas</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Regular items - grid view
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {(catalogs[cat.id] || []).map(item => (
                          <div 
                            key={item.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
                            data-testid={`catalog-item-${item.id}`}
                          >
                            <span className="text-sm text-slate-700">{item.value}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-700 hover:bg-red-50 transition-opacity"
                              onClick={() => setDeleteItem(item)}
                              data-testid={`delete-btn-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {!cat.hasPrice && (!catalogs[cat.id] || catalogs[cat.id].length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        <p>No hay items en esta categoría</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar item?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción eliminará "{deleteItem?.value}" del catálogo.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteItem}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Unified Doses + Schedule Dialog */}
        <Dialog open={!!editingDoses} onOpenChange={() => setEditingDoses(null)}>
          <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ListOrdered className="w-5 h-5" />
                Esquema de Dosis - {editingDoses?.value}
              </DialogTitle>
              <DialogDescription>
                Configura las dosis y los días de intervalo entre cada una. 
                Todo se guarda en un solo paso (catálogo + alertas de próxima dosis).
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Quick add from general doses */}
              {dosisGenerales.filter(d => !dosesList.some(x => x.name === d)).length > 0 && (
                <div>
                  <p className="text-sm text-slate-600 mb-2">Agregar dosis:</p>
                  <div className="flex flex-wrap gap-2">
                    {dosisGenerales.filter(d => !dosesList.some(x => x.name === d)).map((d) => (
                      <Button
                        key={d}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addDose(d)}
                        className="text-xs"
                        data-testid={`quick-add-dose-${d}`}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {d}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom dose input */}
              <div className="flex gap-2">
                <Input
                  value={customDose}
                  onChange={(e) => setCustomDose(e.target.value)}
                  placeholder="Otra dosis (ej: REFUERZO ANUAL)"
                  className="input-clinical"
                  onKeyDown={(e) => e.key === 'Enter' && addDose(customDose)}
                  data-testid="input-custom-dose"
                />
                <Button type="button" variant="outline" onClick={() => addDose(customDose)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Sequence with intervals */}
              {dosesList.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-lg">
                  Sin dosis configuradas. Agrega dosis con los botones de arriba.
                </p>
              ) : (
                <div className="space-y-1">
                  <p className="text-sm text-slate-600 mb-1">Secuencia e intervalos:</p>
                  {dosesList.map((dose, idx) => (
                    <div key={dose.name}>
                      {idx > 0 && (
                        <div className="flex items-center gap-2 py-1 pl-8">
                          <span className="text-slate-400">↓</span>
                          <Input
                            type="number"
                            min="1"
                            value={dose.dias}
                            onChange={(e) => updateDoseDias(idx, e.target.value)}
                            className="w-24 h-8 text-sm"
                            placeholder="días"
                            data-testid={`interval-input-${idx}`}
                          />
                          <span className="text-slate-500 text-xs">
                            días después de la anterior (vacío = sin alerta)
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg" data-testid={`dose-row-${idx}`}>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 font-mono">
                            {idx + 1}
                          </Badge>
                          <span className="text-sm font-medium text-slate-700">{dose.name}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => removeDose(idx)}
                          data-testid={`remove-dose-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Description */}
              <div>
                <p className="text-sm text-slate-600 mb-1">Descripción (opcional):</p>
                <Input
                  value={esquemaDesc}
                  onChange={(e) => setEsquemaDesc(e.target.value)}
                  placeholder="Ej: Esquema 0-1-6 meses"
                  className="input-clinical"
                  data-testid="input-esquema-desc"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingDoses(null)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEsquema} disabled={savingEsquema} data-testid="save-doses-btn">
                {savingEsquema ? 'Guardando...' : 'Guardar Esquema'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CatalogsPage;
